// Curriculum Alignment report builder. Aggregates three sources of educational
// content into one complete, accurate report for a curriculum committee:
//   1. Every game mechanic        — tagged to a framework + an age bracket
//   2. Every decision card        — framework tag + the lesson each choice teaches
//   3. Every mentor advice line   — tagged by its trigger condition
// plus a summary: unique learning moments per bracket per framework.
//
// Pure + dependency-light so it can be unit-tested and rendered as text or JSON.

import { MENTOR_ROSTER, type MentorId, type MentorTrigger } from '@biz-kid/engine';
import { DECK, FRAMEWORKS, type Framework } from '../routes/builder/weeklyDecisionDeck.js';
import { ADVICE, TRIGGER_LABEL } from '../life/mentorAdvice.js';
import { MECHANICS, BRACKETS, APP_VERSION, type Bracket } from './mechanicsCatalog.js';

const FRAMEWORK_ORDER = Object.keys(FRAMEWORKS) as Framework[];
const BRACKET_ORDER: Bracket[] = [1, 2, 3, 4];

// The weekly decision deck surfaces in the Builder MVP (the Explorer bracket, ages
// 11–13), so its lessons are counted under that bracket in the matrix.
const DECISION_BRACKET: Bracket = 2;
// The Mastermind unlocks at age 11 (Explorer); mentor lines belong to that bracket.
const MENTOR_BRACKET: Bracket = 2;

export type MechanicRow = {
  id: string;
  name: string;
  framework: Framework;
  bracket: Bracket;
  bracketLabel: string;
  ages: string;
  unlockAge: number | null;
  description: string;
};

export type DecisionLessonRow = { choice: string; lesson: string; landsInWeeks: number };
export type DecisionCardRow = {
  id: string;
  title: string;
  framework: Framework;
  situation: string;
  lessons: DecisionLessonRow[];
};

export type MentorLineRow = {
  mentorId: MentorId;
  mentorName: string;
  specialty: string;
  trigger: MentorTrigger;
  triggerLabel: string;
  line: string;
};

export type CurriculumReport = {
  meta: {
    title: string;
    version: string;
    generatedAt: string;
    note: string;
    frameworks: Framework[];
    brackets: { id: Bracket; label: string; ages: string }[];
  };
  mechanics: MechanicRow[];
  decisionCards: DecisionCardRow[];
  mentorAdvice: MentorLineRow[];
  summary: {
    // unique learning moments (mechanics + decision-card lessons) per bracket per framework
    momentsByBracketByFramework: Record<Bracket, Record<Framework, number>>;
    bracketTotals: Record<Bracket, number>;
    frameworkTotals: Record<Framework, number>;
    counts: {
      mechanics: number;
      decisionCards: number;
      decisionLessons: number;
      mentorLines: number;
      mentorLinesByTrigger: Record<string, number>;
      uniqueLearningMoments: number; // mechanics + decision lessons + mentor lines
    };
  };
};

/** Build the full structured report. `generatedAt` is injected so the function
 *  stays pure (the screen passes new Date().toISOString()). */
export function buildCurriculumReport(generatedAt = ''): CurriculumReport {
  const mechanics: MechanicRow[] = MECHANICS.map((m) => ({
    id: m.id,
    name: m.name,
    framework: m.framework,
    bracket: m.bracket,
    bracketLabel: BRACKETS[m.bracket].label,
    ages: BRACKETS[m.bracket].ages,
    unlockAge: m.unlockAge ?? null,
    description: m.description,
  }));

  const decisionCards: DecisionCardRow[] = DECK.map((card) => ({
    id: card.id,
    title: card.title,
    framework: card.framework,
    situation: card.situation,
    lessons: card.choices.map((ch) => ({
      choice: ch.label,
      lesson: ch.delayed.lesson,
      landsInWeeks: ch.delayed.weeksAhead,
    })),
  }));

  const mentorAdvice: MentorLineRow[] = [];
  for (const mentor of MENTOR_ROSTER) {
    const lib = ADVICE[mentor.id];
    for (const trigger of Object.keys(lib) as MentorTrigger[]) {
      for (const line of lib[trigger] ?? []) {
        mentorAdvice.push({
          mentorId: mentor.id,
          mentorName: mentor.name,
          specialty: mentor.specialty,
          trigger,
          triggerLabel: TRIGGER_LABEL[trigger],
          line,
        });
      }
    }
  }

  // ── Summary matrix: learning moments per bracket per framework ──────────────
  const momentsByBracketByFramework = emptyMatrix();
  for (const m of mechanics) momentsByBracketByFramework[m.bracket][m.framework] += 1;
  let decisionLessons = 0;
  for (const card of decisionCards) {
    for (const _ of card.lessons) {
      momentsByBracketByFramework[DECISION_BRACKET][card.framework] += 1;
      decisionLessons += 1;
    }
  }

  const bracketTotals = {} as Record<Bracket, number>;
  const frameworkTotals = {} as Record<Framework, number>;
  for (const f of FRAMEWORK_ORDER) frameworkTotals[f] = 0;
  for (const b of BRACKET_ORDER) {
    bracketTotals[b] = 0;
    for (const f of FRAMEWORK_ORDER) {
      const n = momentsByBracketByFramework[b][f];
      bracketTotals[b] += n;
      frameworkTotals[f] += n;
    }
  }

  const mentorLinesByTrigger: Record<string, number> = {};
  for (const row of mentorAdvice) {
    mentorLinesByTrigger[row.trigger] = (mentorLinesByTrigger[row.trigger] ?? 0) + 1;
  }

  const uniqueLearningMoments = mechanics.length + decisionLessons + mentorAdvice.length;

  return {
    meta: {
      title: 'Biz Kid — Curriculum Alignment Report',
      version: APP_VERSION,
      generatedAt,
      note: 'Five money frameworks (Cashflow, Compounding, Risk, Leverage, Reputation) mapped across four learning brackets. Bracket reflects the earliest developmentally-appropriate stage; unlockAge notes any in-game age gate. Decision-card lessons are counted under the Explorer bracket (the Builder MVP, ages 11–13) where the weekly decision system runs; mentor lines are tagged by trigger and counted separately.',
      frameworks: FRAMEWORK_ORDER,
      brackets: BRACKET_ORDER.map((b) => BRACKETS[b]),
    },
    mechanics,
    decisionCards,
    mentorAdvice,
    summary: {
      momentsByBracketByFramework,
      bracketTotals,
      frameworkTotals,
      counts: {
        mechanics: mechanics.length,
        decisionCards: decisionCards.length,
        decisionLessons,
        mentorLines: mentorAdvice.length,
        mentorLinesByTrigger,
        uniqueLearningMoments,
      },
    },
  };
}

function emptyMatrix(): Record<Bracket, Record<Framework, number>> {
  const m = {} as Record<Bracket, Record<Framework, number>>;
  for (const b of BRACKET_ORDER) {
    m[b] = {} as Record<Framework, number>;
    for (const f of FRAMEWORK_ORDER) m[b][f] = 0;
  }
  return m;
}

// ── Plain-text rendering ──────────────────────────────────────────────────────

const RULE = '='.repeat(78);
const rule = '-'.repeat(78);

/** Render the report as a complete, monospaced plain-text document. */
export function renderReportText(r: CurriculumReport): string {
  const L: string[] = [];
  L.push(RULE);
  L.push(r.meta.title);
  L.push(`Version ${r.meta.version}${r.meta.generatedAt ? `   Generated ${r.meta.generatedAt}` : ''}`);
  L.push(RULE);
  L.push(wrap(r.meta.note));
  L.push('');
  L.push('Learning brackets:');
  for (const b of r.meta.brackets) L.push(`  ${b.id}. ${b.label} (ages ${b.ages})`);
  L.push(`Frameworks: ${r.meta.frameworks.join(', ')}`);
  L.push('');

  // 1. Mechanics
  L.push(RULE);
  L.push(`1. GAME MECHANICS  (${r.mechanics.length} total)`);
  L.push('   Each mechanic tagged to one framework and one bracket.');
  L.push(RULE);
  for (const b of r.meta.brackets) {
    const rows = r.mechanics.filter((m) => m.bracket === b.id);
    if (!rows.length) continue;
    L.push('');
    L.push(`[ Bracket ${b.id} — ${b.label} (${b.ages}) ]`);
    for (const m of rows) {
      const gate = m.unlockAge != null ? `  (unlocks age ${m.unlockAge})` : '';
      L.push(`  • ${m.name}  —  ${m.framework}${gate}`);
      L.push(wrap(m.description, '      '));
    }
  }
  L.push('');

  // 2. Decision cards
  L.push(RULE);
  const lessonCount = r.summary.counts.decisionLessons;
  L.push(`2. DECISION CARDS  (${r.decisionCards.length} cards, ${lessonCount} lessons)`);
  L.push('   Each card carries a framework tag; each choice fires a lesson when its');
  L.push('   delayed consequence lands.');
  L.push(RULE);
  for (const f of r.meta.frameworks) {
    const cards = r.decisionCards.filter((c) => c.framework === f);
    if (!cards.length) continue;
    L.push('');
    L.push(`### ${f} (${cards.length} cards)`);
    for (const card of cards) {
      L.push('');
      L.push(`  ${card.title}  [${card.framework}]`);
      L.push(wrap(card.situation, '    '));
      for (const ls of card.lessons) {
        L.push(`    → "${ls.choice}" (lands +${ls.landsInWeeks}w):`);
        L.push(wrap(ls.lesson, '        '));
      }
    }
  }
  L.push('');

  // 3. Mentor advice
  L.push(RULE);
  L.push(`3. MENTOR ADVICE LINES  (${r.mentorAdvice.length} lines)`);
  L.push('   Each line tagged by the trigger condition that makes the mentor speak.');
  L.push(RULE);
  for (const mentor of MENTOR_ROSTER) {
    const rows = r.mentorAdvice.filter((x) => x.mentorId === mentor.id);
    if (!rows.length) continue;
    L.push('');
    L.push(`${mentor.icon} ${mentor.name} — ${mentor.specialty} (${rows.length} lines)`);
    // group by trigger for readability
    const triggers = [...new Set(rows.map((x) => x.trigger))];
    for (const t of triggers) {
      const lines = rows.filter((x) => x.trigger === t);
      L.push(`  trigger: ${t} (${lines[0]!.triggerLabel})`);
      for (const ln of lines) L.push(wrap(`"${ln.line}"`, '    '));
    }
  }
  L.push('');

  // 4. Summary
  L.push(RULE);
  L.push('4. SUMMARY — UNIQUE LEARNING MOMENTS PER BRACKET PER FRAMEWORK');
  L.push('   (mechanics + decision-card lessons)');
  L.push(RULE);
  L.push('');
  L.push(matrixText(r));
  L.push('');
  L.push('Mentor advice lines by trigger:');
  for (const [t, n] of Object.entries(r.summary.counts.mentorLinesByTrigger).sort()) {
    L.push(`  ${t.padEnd(16)} ${n}`);
  }
  L.push('');
  L.push('Totals:');
  L.push(`  Mechanics ................. ${r.summary.counts.mechanics}`);
  L.push(`  Decision cards ........... ${r.summary.counts.decisionCards}`);
  L.push(`  Decision-card lessons .... ${r.summary.counts.decisionLessons}`);
  L.push(`  Mentor advice lines ...... ${r.summary.counts.mentorLines}`);
  L.push(`  ${rule}`);
  L.push(`  Unique learning moments .. ${r.summary.counts.uniqueLearningMoments}`);
  L.push(RULE);
  return L.join('\n');
}

/** ASCII table of the bracket × framework matrix. */
function matrixText(r: CurriculumReport): string {
  const fwCols = r.meta.frameworks;
  const head = ['Bracket'.padEnd(22), ...fwCols.map((f) => f.slice(0, 11).padStart(12)), 'Total'.padStart(8)];
  const lines = [head.join('')];
  lines.push('-'.repeat(head.join('').length));
  for (const b of r.meta.brackets) {
    const cells = [
      `${b.id}.${b.label} ${b.ages}`.padEnd(22),
      ...fwCols.map((f) => String(r.summary.momentsByBracketByFramework[b.id][f]).padStart(12)),
      String(r.summary.bracketTotals[b.id]).padStart(8),
    ];
    lines.push(cells.join(''));
  }
  lines.push('-'.repeat(head.join('').length));
  const totalRow = [
    'TOTAL'.padEnd(22),
    ...fwCols.map((f) => String(r.summary.frameworkTotals[f]).padStart(12)),
    String(fwCols.reduce((s, f) => s + r.summary.frameworkTotals[f], 0)).padStart(8),
  ];
  lines.push(totalRow.join(''));
  return lines.join('\n');
}

/** Soft word-wrap to ~74 cols with an optional indent, for readable text output. */
function wrap(text: string, indent = '  ', width = 74): string {
  const words = text.split(/\s+/);
  const out: string[] = [];
  let line = indent;
  for (const w of words) {
    if (line.length + w.length + 1 > width && line.trim().length) {
      out.push(line);
      line = indent + w;
    } else {
      line += (line === indent ? '' : ' ') + w;
    }
  }
  if (line.trim().length) out.push(line);
  return out.join('\n');
}

export function reportJSON(r: CurriculumReport): string {
  return JSON.stringify(r, null, 2);
}
